import { cn } from "@monorepo-apps/ui/src/lib/utils";

import {
  MILESTONE_STATUS_COLOR,
  MILESTONE_STATUS_LABEL,
  type MilestoneHealth,
} from "@/presentation/data/planner";

interface MilestoneRailProps {
  health: MilestoneHealth[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
}

function countdown(h: MilestoneHealth): string {
  if (h.status === "done") return "complete";
  if (h.days === null) return "no date";
  if (h.days < 0) return `${Math.abs(h.days)}d late`;
  return `in ${h.days}d`;
}

export function MilestoneRail({ health, selectedKey, onSelect }: MilestoneRailProps) {
  return (
    <div className="flex gap-2.5 overflow-x-auto pt-1 pb-1.5">
      {health.map((h) => {
        const color = MILESTONE_STATUS_COLOR[h.status];
        const pct = h.total > 0 ? Math.round((h.done / h.total) * 100) : 0;
        const selected = selectedKey === h.milestone.key;
        const urgent = h.status === "at-risk" || h.status === "breached";
        return (
          <button
            key={h.milestone.key}
            type="button"
            onClick={() => onSelect(selected ? null : h.milestone.key)}
            style={{ borderTopColor: color }}
            className={cn(
              "bg-card relative flex w-[182px] shrink-0 flex-col rounded-xl border border-t-[3px] px-3 py-2.5 text-left transition-shadow",
              selected
                ? "shadow-[0_0_0_2px_var(--primary)]"
                : "border-border hover:shadow-[0_0_0_2px_oklch(0.55_0.17_190_/_0.2)]"
            )}
          >
            <div className="flex items-baseline justify-between">
              <span className="font-data text-[13px] font-semibold">{h.milestone.code}</span>
              <span
                className="font-data text-[9.5px]"
                style={{ color: urgent ? color : "var(--muted-foreground)" }}
              >
                {countdown(h)}
              </span>
            </div>
            <span className="text-muted-foreground mt-1 mb-1.5 line-clamp-2 h-[26px] text-[10.5px] leading-tight">
              {h.milestone.title}
            </span>
            <span
              className="font-data mb-1.5 inline-flex items-center gap-1.5 text-[9px] font-semibold tracking-[0.08em] uppercase"
              style={{ color }}
            >
              <span className="size-[7px] rounded-full" style={{ background: color }} />
              {MILESTONE_STATUS_LABEL[h.status]}
            </span>
            <div className="bg-muted flex h-[5px] overflow-hidden rounded">
              <span style={{ width: `${pct}%`, background: "var(--rag-green)" }} />
            </div>
            <div className="font-data text-muted-foreground mt-1.5 flex gap-2 text-[9px]">
              <span>
                {h.done}/{h.total} done
              </span>
              {h.overdue > 0 ? (
                <span style={{ color: "var(--rag-red)" }}>{h.overdue} late</span>
              ) : null}
              {h.atRisk > 0 ? (
                <span style={{ color: "var(--rag-amber)" }}>{h.atRisk} risk</span>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}
